import React from 'react';
import { useTranslation } from 'react-i18next';
import PhoneInput from '../../../../components/PhoneInput';
import Input from '../../../../components/Input';
import Switch from '../../../../components/Switch';
import Dropdown from '../../../../components/Dropdown';
import TextArea from '../../../../components/TextArea';
import ImageUpload from '../../../../components/ImageUpload';
import { VALIDATION_FIELD_MAPPING } from '../../../../utils/appConstant';
import { Validation } from '../../../../interfaces/common';
import { AddEdit as AddEditInterface } from '../../../../interfaces/user.interface';

interface UserDetailsProps {
  edit: boolean;
  view?: boolean;
  roles: any[];
  allRoles: any[];
  formData: AddEditInterface;
  validation: Validation;
  dropdownSearchData: any;
  onDropdownSearchChange: (e: any, name: string) => void;
  onKeyDown: (e: any, name?: string) => void;
  onInputBlur: (e: any, name: string) => void;
  onInputChange: (e: any, name: string) => void;
  userCountries?: any[];
  userStates?: any[];
  userCities?: any[];
  companyCountries?: any[];
  companyStates?: any[];
  companyCities?: any[];
  areas?: any[];
  allAreas?: any[];
  onRemoveProfileImage?: () => void;
  onRemoveCompanyLogo?: () => void;
}

const UserDetails: React.FC<UserDetailsProps> = ({
  edit,
  view,
  roles,
  allRoles,
  formData,
  validation,
  dropdownSearchData,
  onDropdownSearchChange,
  onKeyDown,
  onInputBlur,
  onInputChange,
  userCountries = [],
  userStates = [],
  userCities = [],
  companyCountries = [],
  companyStates = [],
  companyCities = [],
  areas = [],
  allAreas = [],
  onRemoveProfileImage,
  onRemoveCompanyLogo,
}) => {
  const { t } = useTranslation();

  return (
    <form className="card-body" autoComplete="off">
      {/* Section 1: Primary Details */}
      <div className="row g-6">
        <h6 className="mb-0">1. {t('users.addEdit.pageTexts.form.sections.primaryDetails')}</h6>

        {/* Profile Picture - col-12 */}
        <div className="col-12">
          <ImageUpload
            id={VALIDATION_FIELD_MAPPING.profileImageFile}
            name={VALIDATION_FIELD_MAPPING.profileImageFile}
            disabled={view}
            accept="image/png, image/jpeg, image/gif"
            error={
              !!validation.fields.find(
                (field) => field.field === VALIDATION_FIELD_MAPPING.profileImageFile
              )
            }
            errorMessage={
              validation.fields.find(
                (field) => field.field === VALIDATION_FIELD_MAPPING.profileImageFile
              )?.message ?? ''
            }
            label={t('users.addEdit.pageTexts.form.profileImage.label')}
            value={formData.profileImageFile}
            previewUrl={formData.profileImage}
            enableCrop={true}
            onBlur={(e) => onInputBlur(e, VALIDATION_FIELD_MAPPING.profileImageFile)}
            onChange={(e) => onInputChange(e, VALIDATION_FIELD_MAPPING.profileImageFile)}
            onRemove={onRemoveProfileImage}
          />
        </div>

        {/* First Name - col-md-3 */}
        <div className="col-md-3">
          <div className="form-floating form-floating-outline">
            <Input
              type="text"
              required
              id={VALIDATION_FIELD_MAPPING.firstName}
              name={VALIDATION_FIELD_MAPPING.firstName}
              disabled={view}
              error={
                !!validation.fields.find(
                  (field) => field.field === VALIDATION_FIELD_MAPPING.firstName
                )
              }
              errorMessage={
                validation.fields.find(
                  (field) => field.field === VALIDATION_FIELD_MAPPING.firstName
                )?.message ?? ''
              }
              label={t('users.addEdit.pageTexts.form.firstName.label')}
              placeholder={t('users.addEdit.pageTexts.form.firstName.placeholder')}
              value={formData.firstName}
              onKeyDown={onKeyDown}
              onBlur={(e) => onInputBlur(e, VALIDATION_FIELD_MAPPING.firstName)}
              onChange={(e) => onInputChange(e, VALIDATION_FIELD_MAPPING.firstName)}
            />
          </div>
        </div>

        {/* Last Name - col-md-3 */}
        <div className="col-md-3">
          <div className="form-floating form-floating-outline">
            <Input
              type="text"
              required
              id={VALIDATION_FIELD_MAPPING.lastName}
              name={VALIDATION_FIELD_MAPPING.lastName}
              disabled={view}
              error={
                !!validation.fields.find(
                  (field) => field.field === VALIDATION_FIELD_MAPPING.lastName
                )
              }
              errorMessage={
                validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.lastName)
                  ?.message ?? ''
              }
              label={t('users.addEdit.pageTexts.form.lastName.label')}
              placeholder={t('users.addEdit.pageTexts.form.lastName.placeholder')}
              value={formData.lastName}
              onKeyDown={onKeyDown}
              onBlur={(e) => onInputBlur(e, VALIDATION_FIELD_MAPPING.lastName)}
              onChange={(e) => onInputChange(e, VALIDATION_FIELD_MAPPING.lastName)}
            />
          </div>
        </div>

        {/* Display Name - col-md-3 */}
        <div className="col-md-3">
          <div className="form-floating form-floating-outline">
            <Input
              type="text"
              id={VALIDATION_FIELD_MAPPING.displayName}
              name={VALIDATION_FIELD_MAPPING.displayName}
              disabled={view}
              error={
                !!validation.fields.find(
                  (field) => field.field === VALIDATION_FIELD_MAPPING.displayName
                )
              }
              errorMessage={
                validation.fields.find(
                  (field) => field.field === VALIDATION_FIELD_MAPPING.displayName
                )?.message ?? ''
              }
              label={t('users.addEdit.pageTexts.form.displayName.label')}
              placeholder={t('users.addEdit.pageTexts.form.displayName.placeholder')}
              value={formData.displayName}
              onKeyDown={onKeyDown}
              onBlur={(e) => onInputBlur(e, VALIDATION_FIELD_MAPPING.displayName)}
              onChange={(e) => onInputChange(e, VALIDATION_FIELD_MAPPING.displayName)}
            />
          </div>
        </div>

        {/* Role - col-md-3 */}
        <div className="col-md-3">
          <div className="form-floating form-floating-outline">
            <Dropdown
              values={formData.role && formData.role.toString().trim() ? [formData.role] : []}
              required
              options={(roles || []).map((role: any) => ({
                label: role.name,
                value: role.id,
              }))}
              allOptions={(allRoles || []).map((role: any) => ({
                label: role.name,
                value: role.id,
              }))}
              id={VALIDATION_FIELD_MAPPING.role}
              name={VALIDATION_FIELD_MAPPING.role}
              disabled={view}
              searchText={dropdownSearchData.role}
              onSearch={onDropdownSearchChange}
              placeholder={t('users.addEdit.pageTexts.form.role.placeholder')}
              label={t('users.addEdit.pageTexts.form.role.label')}
              error={
                !!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.role)
              }
              errorMessage={
                validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.role)
                  ?.message ?? ''
              }
              onChange={onInputChange}
              onBlur={onInputBlur}
            />
          </div>
        </div>

        {/* Phone Number - col-md-4 */}
        <div className="col-md-4">
          <div className="form-floating form-floating-outline">
            <PhoneInput
              type="text"
              required
              id={VALIDATION_FIELD_MAPPING.phone}
              name={VALIDATION_FIELD_MAPPING.phone}
              disabled={edit || view}
              error={
                !!validation.fields.find(
                  (field) => field.field === VALIDATION_FIELD_MAPPING.phone
                )
              }
              errorMessage={
                validation.fields.find(
                  (field) => field.field === VALIDATION_FIELD_MAPPING.phone
                )?.message ?? ''
              }
              label={t('users.addEdit.pageTexts.form.phoneNumber.label')}
              placeholder={t('users.addEdit.pageTexts.form.phoneNumber.placeholder')}
              value={(formData.countryCode || '+91') + (formData.phone || '')}
              onKeyDown={onKeyDown}
              onBlur={(e) => onInputBlur(e, VALIDATION_FIELD_MAPPING.phone)}
              onChange={(e) => onInputChange(e, VALIDATION_FIELD_MAPPING.phone)}
            />
          </div>
        </div>

        {/* Email - col-md-4 */}
        <div className="col-md-4">
          <div className="form-floating form-floating-outline">
            <Input
              required
              type="text"
              id={VALIDATION_FIELD_MAPPING.email}
              name={VALIDATION_FIELD_MAPPING.email}
              disabled={edit || view}
              error={
                !!validation.fields.find(
                  (field) => field.field === VALIDATION_FIELD_MAPPING.email
                )
              }
              errorMessage={
                validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.email)
                  ?.message ?? ''
              }
              label={t('users.addEdit.pageTexts.form.email.label')}
              placeholder={t('users.addEdit.pageTexts.form.email.placeholder')}
              value={formData.email}
              onKeyDown={onKeyDown}
              onBlur={(e) => onInputBlur(e, VALIDATION_FIELD_MAPPING.email)}
              onChange={(e) => onInputChange(e, VALIDATION_FIELD_MAPPING.email)}
            />
          </div>
        </div>

        {/* RERA Number - col-md-4 */}
        <div className="col-md-4">
          <div className="form-floating form-floating-outline">
            <Input
              type="text"
              id={VALIDATION_FIELD_MAPPING.reraId}
              name={VALIDATION_FIELD_MAPPING.reraId}
              disabled={view}
              label={t('users.addEdit.pageTexts.form.reraId.label')}
              placeholder={t('users.addEdit.pageTexts.form.reraId.placeholder')}
              value={formData.reraId}
              onKeyDown={onKeyDown}
              onBlur={(e) => onInputBlur(e, VALIDATION_FIELD_MAPPING.reraId)}
              onChange={(e) => onInputChange(e, VALIDATION_FIELD_MAPPING.reraId)}
            />
          </div>
        </div>
      </div>

      <hr className="my-6 mx-n4" />

      {/* Section 2: Address Details */}
      <div className="row g-6">
        <h6 className="mb-0">2. {t('users.addEdit.pageTexts.form.sections.addressDetails')}</h6>

        {/* Address Line 1 - col-md-4 */}
        <div className="col-md-4">
          <div className="form-floating form-floating-outline">
            <Input
              type="text"
              id={VALIDATION_FIELD_MAPPING.addressLine1}
              name={VALIDATION_FIELD_MAPPING.addressLine1}
              disabled={view}
              label={t('users.addEdit.pageTexts.form.addressLine1.label')}
              placeholder={t('users.addEdit.pageTexts.form.addressLine1.placeholder')}
              value={formData.addressLine1}
              onKeyDown={onKeyDown}
              onBlur={(e) => onInputBlur(e, VALIDATION_FIELD_MAPPING.addressLine1)}
              onChange={(e) => onInputChange(e, VALIDATION_FIELD_MAPPING.addressLine1)}
            />
          </div>
        </div>

        {/* Address Line 2 - col-md-4 */}
        <div className="col-md-4">
          <div className="form-floating form-floating-outline">
            <Input
              type="text"
              id={VALIDATION_FIELD_MAPPING.addressLine2}
              name={VALIDATION_FIELD_MAPPING.addressLine2}
              disabled={view}
              label={t('users.addEdit.pageTexts.form.addressLine2.label')}
              placeholder={t('users.addEdit.pageTexts.form.addressLine2.placeholder')}
              value={formData.addressLine2}
              onKeyDown={onKeyDown}
              onBlur={(e) => onInputBlur(e, VALIDATION_FIELD_MAPPING.addressLine2)}
              onChange={(e) => onInputChange(e, VALIDATION_FIELD_MAPPING.addressLine2)}
            />
          </div>
        </div>

        {/* Pincode - col-md-4 */}
        <div className="col-md-4">
          <div className="form-floating form-floating-outline">
            <Input
              type="text"
              id={VALIDATION_FIELD_MAPPING.userPincode}
              name={VALIDATION_FIELD_MAPPING.userPincode}
              disabled={view}
              label={t('users.addEdit.pageTexts.form.pincode.label')}
              placeholder={t('users.addEdit.pageTexts.form.pincode.placeholder')}
              value={formData.pincode}
              onKeyDown={onKeyDown}
              onBlur={(e) => onInputBlur(e, VALIDATION_FIELD_MAPPING.userPincode)}
              onChange={(e) => onInputChange(e, VALIDATION_FIELD_MAPPING.userPincode)}
            />
          </div>
        </div>
      </div>

      <hr className="my-6 mx-n4" />

      {/* Section 3: Working Area Details */}
      <div className="row g-6">
        <h6 className="mb-0">3. {t('users.addEdit.pageTexts.form.sections.workingAreaDetails')}</h6>

        {/* Country - col-md-3 */}
        <div className="col-md-3">
          <div className="form-floating form-floating-outline">
            <Dropdown
              values={
                formData.countryId && formData.countryId.toString().trim()
                  ? [formData.countryId]
                  : []
              }
              options={(userCountries || []).map((country: any) => ({
                label: country.name,
                value: country.id,
              }))}
              allOptions={(userCountries || []).map((country: any) => ({
                label: country.name,
                value: country.id,
              }))}
              id={VALIDATION_FIELD_MAPPING.countryId}
              name={VALIDATION_FIELD_MAPPING.countryId}
              disabled={view}
              required
              searchText={dropdownSearchData.country}
              onSearch={onDropdownSearchChange}
              placeholder={t('users.addEdit.pageTexts.form.country.placeholder')}
              label={t('users.addEdit.pageTexts.form.country.label')}
              error={
                !!validation.fields.find(
                  (field) => field.field === VALIDATION_FIELD_MAPPING.countryId
                )
              }
              errorMessage={
                validation.fields.find(
                  (field) => field.field === VALIDATION_FIELD_MAPPING.countryId
                )?.message ?? ''
              }
              onChange={onInputChange}
              onBlur={onInputBlur}
            />
          </div>
        </div>

        {/* State - col-md-3 */}
        <div className="col-md-3">
          <div className="form-floating form-floating-outline">
            <Dropdown
              values={
                formData.stateId && formData.stateId.toString().trim() ? [formData.stateId] : []
              }
              options={(userStates || []).map((state: any) => ({
                label: state.name,
                value: state.id,
              }))}
              allOptions={(userStates || []).map((state: any) => ({
                label: state.name,
                value: state.id,
              }))}
              id={VALIDATION_FIELD_MAPPING.stateId}
              name={VALIDATION_FIELD_MAPPING.stateId}
              disabled={view}
              required
              searchText={dropdownSearchData.state}
              onSearch={onDropdownSearchChange}
              placeholder={t('users.addEdit.pageTexts.form.state.placeholder')}
              label={t('users.addEdit.pageTexts.form.state.label')}
              error={
                !!validation.fields.find(
                  (field) => field.field === VALIDATION_FIELD_MAPPING.stateId
                )
              }
              errorMessage={
                validation.fields.find(
                  (field) => field.field === VALIDATION_FIELD_MAPPING.stateId
                )?.message ?? ''
              }
              onChange={onInputChange}
              onBlur={onInputBlur}
            />
          </div>
        </div>

        {/* City - col-md-3 */}
        <div className="col-md-3">
          <div className="form-floating form-floating-outline">
            <Dropdown
              values={formData.cityId || []}
              multiSelect
              options={(userCities || []).map((city: any) => ({
                label: city.name,
                value: String(city.id),
              }))}
              allOptions={(userCities || []).map((city: any) => ({
                label: city.name,
                value: String(city.id),
              }))}
              id={VALIDATION_FIELD_MAPPING.cityId}
              name={VALIDATION_FIELD_MAPPING.cityId}
              disabled={view}
              required
              searchText={dropdownSearchData.city}
              onSearch={onDropdownSearchChange}
              placeholder={t('users.addEdit.pageTexts.form.city.placeholder')}
              label={t('users.addEdit.pageTexts.form.city.label')}
              error={
                !!validation.fields.find(
                  (field) => field.field === VALIDATION_FIELD_MAPPING.cityId
                )
              }
              errorMessage={
                validation.fields.find(
                  (field) => field.field === VALIDATION_FIELD_MAPPING.cityId
                )?.message ?? ''
              }
              onChange={onInputChange}
              onBlur={onInputBlur}
            />
          </div>
        </div>

        {/* Area - col-md-3 */}
        <div className="col-md-3">
            <div className="form-floating form-floating-outline">
                <Dropdown
                values={formData.area || []}
                multiSelect
                options={(areas || []).map((area: any) => ({
                    label: area.name,
                    value: String(area.id),
                }))}
                allOptions={(allAreas || []).map((area: any) => ({
                    label: area.name,
                    value: String(area.id),
                }))}
                id={VALIDATION_FIELD_MAPPING.area}
                name={VALIDATION_FIELD_MAPPING.area}
                disabled={view}
                required
                searchText={dropdownSearchData.area}
                onSearch={onDropdownSearchChange}
                placeholder={t('users.addEdit.pageTexts.form.area.placeholder')}
                label={t('users.addEdit.pageTexts.form.area.label')}
                error={
                    !!validation.fields.find(
                    (field) => field.field === VALIDATION_FIELD_MAPPING.area
                    )
                }
                errorMessage={
                    validation.fields.find(
                    (field) => field.field === VALIDATION_FIELD_MAPPING.area
                    )?.message ?? ''
                }
                onChange={onInputChange}
                onBlur={onInputBlur}
                />
            </div>
        </div>
      </div>

      <hr className="my-6 mx-n4" />

      {/* Section 4: Company Details */}
      <div className="row g-6">
        <h6 className="mb-0">4. {t('users.addEdit.pageTexts.form.sections.companyDetails')}</h6>

        {/* Company Logo - col-12 */}
        <div className="col-12">
          <ImageUpload
            id={VALIDATION_FIELD_MAPPING.companyLogoFile}
            name={VALIDATION_FIELD_MAPPING.companyLogoFile}
            disabled={view}
            accept="image/png, image/jpeg, image/gif"
            error={
              !!validation.fields.find(
                (field) => field.field === VALIDATION_FIELD_MAPPING.companyLogoFile
              )
            }
            errorMessage={
              validation.fields.find(
                (field) => field.field === VALIDATION_FIELD_MAPPING.companyLogoFile
              )?.message ?? ''
            }
            label={t('users.addEdit.pageTexts.form.companyLogo.label')}
            value={formData.companyLogoFile}
            previewUrl={formData.companyLogo}
            enableCrop={true}
            onBlur={(e) => onInputBlur(e, VALIDATION_FIELD_MAPPING.companyLogoFile)}
            onChange={(e) => onInputChange(e, VALIDATION_FIELD_MAPPING.companyLogoFile)}
            onRemove={onRemoveCompanyLogo}
          />
        </div>

        {/* Company Name - col-md-6 */}
        <div className="col-md-6">
          <div className="form-floating form-floating-outline">
            <Input
              type="text"
              id={VALIDATION_FIELD_MAPPING.companyName}
              name={VALIDATION_FIELD_MAPPING.companyName}
              disabled={view}
              error={
                !!validation.fields.find(
                  (field) => field.field === VALIDATION_FIELD_MAPPING.companyName
                )
              }
              errorMessage={
                validation.fields.find(
                  (field) => field.field === VALIDATION_FIELD_MAPPING.companyName
                )?.message ?? ''
              }
              label={t('users.addEdit.pageTexts.form.companyName.label')}
              placeholder={t('users.addEdit.pageTexts.form.companyName.placeholder')}
              value={formData.companyName}
              onKeyDown={onKeyDown}
              onBlur={(e) => onInputBlur(e, VALIDATION_FIELD_MAPPING.companyName)}
              onChange={(e) => onInputChange(e, VALIDATION_FIELD_MAPPING.companyName)}
            />
          </div>
        </div>

        {/* Company Contact Number - col-md-6 */}
        <div className="col-md-6">
          <div className="form-floating form-floating-outline">
            <PhoneInput
              type="text"
              id={VALIDATION_FIELD_MAPPING.companyContactNumber}
              name={VALIDATION_FIELD_MAPPING.companyContactNumber}
              disabled={view}
              error={
                !!validation.fields.find(
                  (field) => field.field === VALIDATION_FIELD_MAPPING.companyContactNumber
                )
              }
              errorMessage={
                validation.fields.find(
                  (field) => field.field === VALIDATION_FIELD_MAPPING.companyContactNumber
                )?.message ?? ''
              }
              label={t('users.addEdit.pageTexts.form.companyContactNumber.label')}
              placeholder={t('users.addEdit.pageTexts.form.companyContactNumber.placeholder')}
              value={(formData.companyCountryCode || '+91') + (formData.companyContactNumber || '')}
              onKeyDown={onKeyDown}
              onBlur={(e) => onInputBlur(e, VALIDATION_FIELD_MAPPING.companyContactNumber)}
              onChange={(e) => onInputChange(e, VALIDATION_FIELD_MAPPING.companyContactNumber)}
            />
          </div>
        </div>

        {/* Company Address Line 1 - col-md-6 */}
        <div className="col-md-6">
          <div className="form-floating form-floating-outline">
            <Input
              type="text"
              id={VALIDATION_FIELD_MAPPING.companyAddressLine1}
              name={VALIDATION_FIELD_MAPPING.companyAddressLine1}
              disabled={view}
              error={
                !!validation.fields.find(
                  (field) => field.field === VALIDATION_FIELD_MAPPING.companyAddressLine1
                )
              }
              errorMessage={
                validation.fields.find(
                  (field) => field.field === VALIDATION_FIELD_MAPPING.companyAddressLine1
                )?.message ?? ''
              }
              label={t('users.addEdit.pageTexts.form.companyAddressLine1.label')}
              placeholder={t('users.addEdit.pageTexts.form.companyAddressLine1.placeholder')}
              value={formData.companyAddressLine1}
              onKeyDown={onKeyDown}
              onBlur={(e) => onInputBlur(e, VALIDATION_FIELD_MAPPING.companyAddressLine1)}
              onChange={(e) => onInputChange(e, VALIDATION_FIELD_MAPPING.companyAddressLine1)}
            />
          </div>
        </div>

        {/* Company Address Line 2 - col-md-6 */}
        <div className="col-md-6">
          <div className="form-floating form-floating-outline">
            <Input
              type="text"
              id={VALIDATION_FIELD_MAPPING.companyAddressLine2}
              name={VALIDATION_FIELD_MAPPING.companyAddressLine2}
              disabled={view}
              error={
                !!validation.fields.find(
                  (field) => field.field === VALIDATION_FIELD_MAPPING.companyAddressLine2
                )
              }
              errorMessage={
                validation.fields.find(
                  (field) => field.field === VALIDATION_FIELD_MAPPING.companyAddressLine2
                )?.message ?? ''
              }
              label={t('users.addEdit.pageTexts.form.companyAddressLine2.label')}
              placeholder={t('users.addEdit.pageTexts.form.companyAddressLine2.placeholder')}
              value={formData.companyAddressLine2}
              onKeyDown={onKeyDown}
              onBlur={(e) => onInputBlur(e, VALIDATION_FIELD_MAPPING.companyAddressLine2)}
              onChange={(e) => onInputChange(e, VALIDATION_FIELD_MAPPING.companyAddressLine2)}
            />
          </div>
        </div>

        {/* Company Country - col-md-3 */}
        <div className="col-md-3">
          <div className="form-floating form-floating-outline">
            <Dropdown
              values={
                formData.companyCountryId && formData.companyCountryId.toString().trim()
                  ? [formData.companyCountryId]
                  : []
              }
              options={(companyCountries || []).map((country: any) => ({
                label: country.name,
                value: country.id,
              }))}
              allOptions={(companyCountries || []).map((country: any) => ({
                label: country.name,
                value: country.id,
              }))}
              id={VALIDATION_FIELD_MAPPING.companyCountryId}
              name={VALIDATION_FIELD_MAPPING.companyCountryId}
              disabled={view}
              searchText={dropdownSearchData.companyCountry}
              onSearch={onDropdownSearchChange}
              placeholder={t('users.addEdit.pageTexts.form.companyCountry.placeholder')}
              label={t('users.addEdit.pageTexts.form.companyCountry.label')}
              error={
                !!validation.fields.find(
                  (field) => field.field === VALIDATION_FIELD_MAPPING.companyCountryId
                )
              }
              errorMessage={
                validation.fields.find(
                  (field) => field.field === VALIDATION_FIELD_MAPPING.companyCountryId
                )?.message ?? ''
              }
              onChange={onInputChange}
              onBlur={onInputBlur}
            />
          </div>
        </div>

        {/* Company State - col-md-3 */}
        <div className="col-md-3">
          <div className="form-floating form-floating-outline">
            <Dropdown
              values={
                formData.companyStateId && formData.companyStateId.toString().trim()
                  ? [formData.companyStateId]
                  : []
              }
              options={(companyStates || []).map((state: any) => ({
                label: state.name,
                value: state.id,
              }))}
              allOptions={(companyStates || []).map((state: any) => ({
                label: state.name,
                value: state.id,
              }))}
              id={VALIDATION_FIELD_MAPPING.companyStateId}
              name={VALIDATION_FIELD_MAPPING.companyStateId}
              disabled={view}
              searchText={dropdownSearchData.companyState}
              onSearch={onDropdownSearchChange}
              placeholder={t('users.addEdit.pageTexts.form.companyState.placeholder')}
              label={t('users.addEdit.pageTexts.form.companyState.label')}
              error={
                !!validation.fields.find(
                  (field) => field.field === VALIDATION_FIELD_MAPPING.companyStateId
                )
              }
              errorMessage={
                validation.fields.find(
                  (field) => field.field === VALIDATION_FIELD_MAPPING.companyStateId
                )?.message ?? ''
              }
              onChange={onInputChange}
              onBlur={onInputBlur}
            />
          </div>
        </div>

        {/* Company City - col-md-3 */}
        <div className="col-md-3">
          <div className="form-floating form-floating-outline">
            <Dropdown
              values={
                formData.companyCityId && formData.companyCityId.toString().trim()
                  ? [formData.companyCityId]
                  : []
              }
              options={(companyCities || []).map((city: any) => ({
                label: city.name,
                value: city.id,
              }))}
              allOptions={(companyCities || []).map((city: any) => ({
                label: city.name,
                value: city.id,
              }))}
              id={VALIDATION_FIELD_MAPPING.companyCityId}
              name={VALIDATION_FIELD_MAPPING.companyCityId}
              disabled={view}
              searchText={dropdownSearchData.companyCity}
              onSearch={onDropdownSearchChange}
              placeholder={t('users.addEdit.pageTexts.form.companyCity.placeholder')}
              label={t('users.addEdit.pageTexts.form.companyCity.label')}
              error={
                !!validation.fields.find(
                  (field) => field.field === VALIDATION_FIELD_MAPPING.companyCityId
                )
              }
              errorMessage={
                validation.fields.find(
                  (field) => field.field === VALIDATION_FIELD_MAPPING.companyCityId
                )?.message ?? ''
              }
              onChange={onInputChange}
              onBlur={onInputBlur}
            />
          </div>
        </div>

        {/* Company Pincode - col-md-3 */}
        <div className="col-md-3">
          <div className="form-floating form-floating-outline">
            <Input
              type="text"
              id={VALIDATION_FIELD_MAPPING.companyPincode}
              name={VALIDATION_FIELD_MAPPING.companyPincode}
              disabled={view}
              error={
                !!validation.fields.find(
                  (field) => field.field === VALIDATION_FIELD_MAPPING.companyPincode
                )
              }
              errorMessage={
                validation.fields.find(
                  (field) => field.field === VALIDATION_FIELD_MAPPING.companyPincode
                )?.message ?? ''
              }
              label={t('users.addEdit.pageTexts.form.companyPincode.label')}
              placeholder={t('users.addEdit.pageTexts.form.companyPincode.placeholder')}
              value={formData.companyPincode}
              onKeyDown={onKeyDown}
              onBlur={(e) => onInputBlur(e, VALIDATION_FIELD_MAPPING.companyPincode)}
              onChange={(e) => onInputChange(e, VALIDATION_FIELD_MAPPING.companyPincode)}
            />
          </div>
        </div>
      </div>

      <hr className="my-6 mx-n4" />

      {/* Section 5: Additional Information */}
      <div className="row g-6">
        <h6 className="mb-0">5. {t('users.addEdit.pageTexts.form.sections.otherInformation')}</h6>

        {/* Notes - col-md-9 */}
        <div className="col-md-9">
          <div className="form-floating form-floating-outline">
            <TextArea
              id={VALIDATION_FIELD_MAPPING.notes}
              name={VALIDATION_FIELD_MAPPING.notes}
              disabled={view}
              error={!!validation.fields.find((f) => f.field === VALIDATION_FIELD_MAPPING.notes)}
              errorMessage={
                validation.fields.find((f) => f.field === VALIDATION_FIELD_MAPPING.notes)
                  ?.message ?? ''
              }
              label={t('users.addEdit.pageTexts.form.notes.label')}
              placeholder={t('users.addEdit.pageTexts.form.notes.placeholder')}
              value={formData.notes}
              onKeyDown={onKeyDown}
              onBlur={(e) => onInputBlur(e, VALIDATION_FIELD_MAPPING.notes)}
              onChange={(e) => onInputChange(e, VALIDATION_FIELD_MAPPING.notes)}
              rows={4}
              className="form-control-lg h-100"
            />
          </div>
        </div>

        {/* Status Toggle - col-md-3 */}
        <div className="col-md-3 d-flex align-items-start">
          <div className="form-floating form-floating-outline">
            <Switch
              name={VALIDATION_FIELD_MAPPING.status}
              label={
                formData.status
                  ? t('users.addEdit.pageTexts.form.status.activeLabel')
                  : t('users.addEdit.pageTexts.form.status.inActiveLabel')
              }
              checked={formData.status}
              disabled={view}
              onChange={(e) => onInputChange(e, VALIDATION_FIELD_MAPPING.status)}
            />
          </div>
        </div>
      </div>
    </form>
  );
};

export default UserDetails;
